/**
 * RecordingsPage — Dedicated page for viewing and managing Plaud recordings.
 *
 * Features:
 * - Split-pane layout: recordings list (left) + detail view (right).
 *   On phones the two panes become a master → detail flow instead.
 * - Work vs Personal categorization, read from the backend (never re-derived here)
 * - Date grouping: Today, Yesterday, This Week, This Month, Earlier
 * - GAS-share triage (boundary decision #8): which executive summaries reach
 *   the in-tenant GAS dashboard
 * - Bulk copy + Google Docs / NotebookLM export
 *
 * Route: /recordings (admin only)
 */

import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import {
  Search, Lock, RefreshCw, AlertCircle, Calendar, Download, FileJson, Loader2, Check,
  CloudDownload, ChevronDown,
} from 'lucide-react';
import apiClient from '../../lib/apiClient';
import RecordingsList from './RecordingsList';
import RecordingDetail from './RecordingDetail';
import BulkCopyModal from './BulkCopyModal';
import { SHARE_STATES } from './shareStatus';
import { DATE_RANGES, rangeStart, filterByDateRange, countUndated } from './dateRanges';
import './RecordingsPage.css';

const PAGE_SIZE = 100;

function RecordingsPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all'); // 'all' | 'work' | 'personal' | 'unknown'
  const [shareFilter, setShareFilter] = useState('all'); // 'all' | 'pending' | 'approved' | 'excluded'
  const [accountFilter, setAccountFilter] = useState('all'); // 'all' | 'unassigned' | <account_id>
  const [dateRange, setDateRange] = useState('all');
  const [selectedRecording, setSelectedRecording] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [showBulkCopy, setShowBulkCopy] = useState(false);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportSuccess, setExportSuccess] = useState(false);
  const [exportError, setExportError] = useState(null);
  const [limit, setLimit] = useState(PAGE_SIZE);
  // Phones show one pane at a time; this decides which. Desktop ignores it.
  const [mobilePane, setMobilePane] = useState('list');
  const queryClient = useQueryClient();

  // Fetch recordings for the current user. The endpoint hard-caps each request
  // at 100 rows, so the old single `limit=100` call silently dropped everything
  // past the first hundred — and every count, filter and export below inherited
  // that lie. Walk offsets instead and concatenate, keeping the flat
  // { recordings } cache shape the optimistic mutations write into.
  const { data, isLoading, isFetching, error, refetch } = useQuery({
    queryKey: ['plaud-recordings-all', limit],
    queryFn: async () => {
      const all = [];
      let complete = true;
      for (let offset = 0; offset < limit; offset += PAGE_SIZE) {
        const res = await apiClient.get(`/api/plaud/recordings?limit=${PAGE_SIZE}&offset=${offset}`);
        const batch = res?.recordings || [];
        all.push(...batch);
        if (batch.length < PAGE_SIZE) { complete = true; break; }
        complete = false; // a full page came back — there may be more behind it
      }
      return { recordings: all, complete };
    },
    staleTime: 60_000,
    placeholderData: keepPreviousData,
  });

  const recordings = useMemo(() => data?.recordings || [], [data]);
  const mayHaveMore = data ? !data.complete : false;

  /**
   * Categorization is the BACKEND's answer (plaudService.categorizeRecording),
   * persisted as category / category_source / category_confidence. This page
   * used to re-derive it from a second, weaker keyword list, which meant the
   * badge on screen could disagree with the export, the MCP tools and GAS.
   * Read the stored values; never invent them here.
   */
  const enrichedRecordings = useMemo(() => recordings.map(r => ({
    ...r,
    categoryInfo: {
      category: r.category || null,
      confidence: r.category_source === 'user' ? 1 : (r.category_confidence ?? 0),
      source: r.category_source || 'auto',
    },
  })), [recordings]);

  // Everything except the date range. Kept separate so the page can say
  // exactly how many rows the date filter is hiding.
  const matchesNonDateFilters = useMemo(() => {
    let result = enrichedRecordings;

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(r =>
        r.name?.toLowerCase().includes(query) ||
        r.summary?.toLowerCase().includes(query)
      );
    }

    if (categoryFilter === 'unknown') {
      result = result.filter(r => !r.categoryInfo.category);
    } else if (categoryFilter !== 'all') {
      result = result.filter(r => r.categoryInfo.category === categoryFilter);
    }

    if (shareFilter !== 'all') {
      result = result.filter(r => (r.tenant_share_status || 'pending') === shareFilter);
    }

    // "Needs an account" is the GAS-facing question, so an unconfirmed
    // auto-guess belongs in the queue too — it won't reach GAS either.
    if (accountFilter === 'unassigned') {
      result = result.filter(r => !r.account_id || r.account_source !== 'user');
    } else if (accountFilter !== 'all') {
      result = result.filter(r => r.account_id === accountFilter);
    }

    return result;
  }, [enrichedRecordings, searchQuery, categoryFilter, shareFilter, accountFilter]);

  const filteredRecordings = useMemo(
    () => filterByDateRange(matchesNonDateFilters, dateRange),
    [matchesNonDateFilters, dateRange]
  );

  // Recordings with no usable recorded_at can't be placed inside a bounded
  // range, so they drop out of every one. They used to do that silently.
  const hiddenUndated = useMemo(
    () => (dateRange === 'all' ? 0 : countUndated(matchesNonDateFilters)),
    [matchesNonDateFilters, dateRange]
  );

  // Counts for the filter chips. Date-scoped so the numbers match what the
  // date picker has narrowed to.
  const counts = useMemo(() => {
    const inRange = filterByDateRange(enrichedRecordings, dateRange);
    return {
      all: inRange.length,
      work: inRange.filter(r => r.categoryInfo.category === 'work').length,
      personal: inRange.filter(r => r.categoryInfo.category === 'personal').length,
      unknown: inRange.filter(r => !r.categoryInfo.category).length,
      unassigned: inRange.filter(r => !r.account_id || r.account_source !== 'user').length,
      pending: inRange.filter(r => (r.tenant_share_status || 'pending') === 'pending').length,
      approved: inRange.filter(r => r.tenant_share_status === 'approved').length,
      excluded: inRange.filter(r => r.tenant_share_status === 'excluded').length,
    };
  }, [enrichedRecordings, dateRange]);

  /**
   * Selection follows what's on screen. Without this, selecting everything
   * under one filter and then switching filters left a hidden selection that
   * Copy All would silently include, while "Select all" showed the wrong state.
   */
  useEffect(() => {
    setSelectedIds(prev => {
      if (prev.size === 0) return prev;
      const visible = new Set(filteredRecordings.map(r => r.id));
      const next = new Set();
      prev.forEach(id => { if (visible.has(id)) next.add(id); });
      return next.size === prev.size ? prev : next;
    });
  }, [filteredRecordings]);

  const handleToggleSelect = useCallback((recordingId) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(recordingId)) next.delete(recordingId);
      else next.add(recordingId);
      return next;
    });
  }, []);

  const handleSelectAll = useCallback(() => {
    setSelectedIds(prev =>
      prev.size === filteredRecordings.length
        ? new Set()
        : new Set(filteredRecordings.map(r => r.id))
    );
  }, [filteredRecordings]);

  // Selecting a recording swaps the phone view to the detail pane.
  const handleSelectRecording = useCallback((recording) => {
    setSelectedRecording(recording);
    setMobilePane('detail');
  }, []);

  const handleBackToList = useCallback(() => setMobilePane('list'), []);

  // Handle category update
  const updateCategoryMutation = useMutation({
    mutationFn: async ({ recordingId, category }) => {
      return apiClient.put(`/api/plaud/recordings/${recordingId}/category`, { category });
    },
    onMutate: async ({ recordingId, category }) => {
      // Optimistic — apply the new category immediately so the dropdown doesn't snap back.
      const key = ['plaud-recordings-all', limit];
      const previous = queryClient.getQueryData(key);
      if (previous) {
        queryClient.setQueryData(key, {
          ...previous,
          recordings: (previous.recordings || []).map(r =>
            r.id === recordingId
              ? { ...r, category, category_source: 'user', category_confidence: 1 }
              : r
          ),
        });
      }
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      // Roll back optimistic update on failure.
      if (ctx?.previous) queryClient.setQueryData(['plaud-recordings-all', limit], ctx.previous);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['plaud-recordings-all'] });
    },
  });

  const handleCategoryChange = (recordingId, category) => {
    updateCategoryMutation.mutate({ recordingId, category });
  };

  // GAS-share triage (boundary decision #8): approve/exclude which executive
  // summaries become visible in the in-tenant GAS dashboard. Optimistic with
  // rollback, per the TekStak optimistic-UI standard.
  const updateShareMutation = useMutation({
    mutationFn: async ({ recordingId, status }) => {
      return apiClient.put(`/api/plaud/recordings/${recordingId}/share`, { status });
    },
    onMutate: async ({ recordingId, status }) => {
      const key = ['plaud-recordings-all', limit];
      const previous = queryClient.getQueryData(key);
      if (previous) {
        queryClient.setQueryData(key, {
          ...previous,
          recordings: (previous.recordings || []).map(r =>
            r.id === recordingId
              ? {
                  ...r,
                  tenant_share_status: status,
                  tenant_share_decided_at: status === 'pending' ? null : new Date().toISOString(),
                }
              : r
          ),
        });
      }
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) queryClient.setQueryData(['plaud-recordings-all', limit], ctx.previous);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['plaud-recordings-all'] });
    },
  });

  const handleShareChange = (recordingId, status) => {
    updateShareMutation.mutate({ recordingId, status });
  };

  // Accounts for the picker. Classification lives in TekStak so the GAS
  // Meeting Notes tab can file a summary by departments.acronym instead of
  // guessing from the account name.
  const { data: accountsData } = useQuery({
    queryKey: ['plaud-accounts'],
    queryFn: () => apiClient.get('/api/plaud/accounts'),
    staleTime: 10 * 60_000,
  });
  const accounts = useMemo(() => accountsData?.accounts || [], [accountsData]);

  const updateAccountMutation = useMutation({
    mutationFn: async ({ recordingId, accountId }) =>
      apiClient.put(`/api/plaud/recordings/${recordingId}/link`, { account_id: accountId }),
    onMutate: async ({ recordingId, accountId }) => {
      const key = ['plaud-recordings-all', limit];
      const previous = queryClient.getQueryData(key);
      const picked = accounts.find(a => a.id === accountId) || null;
      if (previous) {
        queryClient.setQueryData(key, {
          ...previous,
          recordings: (previous.recordings || []).map(r =>
            r.id === recordingId
              ? {
                  ...r,
                  account_id: accountId,
                  account_name: picked?.name || null,
                  account_acronym: picked?.acronym || null,
                  // Choosing (or confirming) in this UI is the human decision
                  // that lets the summary reach GAS.
                  account_source: accountId ? 'user' : null,
                }
              : r
          ),
        });
      }
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) queryClient.setQueryData(['plaud-recordings-all', limit], ctx.previous);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['plaud-recordings-all'] });
    },
  });

  const handleAccountChange = (recordingId, accountId) => {
    updateAccountMutation.mutate({ recordingId, accountId });
  };

  const accountPendingId = updateAccountMutation.isPending
    ? updateAccountMutation.variables?.recordingId
    : null;

  const sharePendingId = updateShareMutation.isPending
    ? updateShareMutation.variables?.recordingId
    : null;

  // Participants — who was in the meeting (decision #12). Typed names, the
  // shared MeetingTagBar chips; optimistic with rollback like every other
  // per-recording write on this page.
  const updateParticipantsMutation = useMutation({
    mutationFn: async ({ recordingId, participants }) =>
      apiClient.put(`/api/plaud/recordings/${recordingId}/participants`, { participants }),
    onMutate: async ({ recordingId, participants }) => {
      const key = ['plaud-recordings-all', limit];
      const previous = queryClient.getQueryData(key);
      if (previous) {
        queryClient.setQueryData(key, {
          ...previous,
          recordings: (previous.recordings || []).map(r =>
            r.id === recordingId ? { ...r, participants } : r
          ),
        });
      }
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) queryClient.setQueryData(['plaud-recordings-all', limit], ctx.previous);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['plaud-recordings-all'] });
    },
  });

  const handleParticipantsChange = (recordingId, participants) => {
    updateParticipantsMutation.mutate({ recordingId, participants });
  };

  const participantsPendingId = updateParticipantsMutation.isPending
    ? updateParticipantsMutation.variables?.recordingId
    : null;

  // Manual Plaud sync: pulls new recordings from Plaud into TekStak right
  // now instead of waiting for the hourly scheduler (also backfills AI
  // notes that weren't ready at first import).
  const syncMutation = useMutation({
    mutationFn: () => apiClient.post('/api/plaud/sync/trigger', {}),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['plaud-recordings-all'] });
    },
  });
  const syncResult = syncMutation.data;
  const syncError = syncMutation.error;

  // Export to Google Docs (download JSON for Apps Script)
  const handleExport = useCallback(async (exportCategory = 'work') => {
    setExporting(true);
    setShowExportMenu(false);
    setExportError(null);
    try {
      const params = new URLSearchParams({
        category: exportCategory,
        limit: String(PAGE_SIZE),
      });

      const dateFrom = rangeStart(dateRange);
      if (dateFrom) {
        params.set('dateFrom', dateFrom.toISOString().split('T')[0]);
      }

      const token = await apiClient.getToken();
      const response = await fetch(
        `${apiClient.baseUrl || ''}/api/meeting-export/json-download?${params}`,
        {
          headers: {
            ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
          },
          credentials: 'include',
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || errorData.message || `Export failed (${response.status})`);
      }

      // Get filename from header or generate one
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = `meeting_export_${new Date().toISOString().split('T')[0]}.json`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?([^"]+)"?/);
        if (match) filename = match[1];
      }

      // Download the file
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      setExportSuccess(true);
      setTimeout(() => setExportSuccess(false), 2000);
    } catch (err) {
      console.error('Export failed:', err);
      setExportError(err.message);
    } finally {
      setExporting(false);
    }
  }, [dateRange]);

  // Get selected recordings for bulk operations
  const selectedRecordings = useMemo(() => {
    return enrichedRecordings.filter(r => selectedIds.has(r.id));
  }, [enrichedRecordings, selectedIds]);

  // Resolve the selected recording against the live cache so the detail
  // pane reflects optimistic category updates instead of a stale snapshot.
  const liveSelectedRecording = useMemo(() => {
    if (!selectedRecording) return null;
    return enrichedRecordings.find(r => r.id === selectedRecording.id) || selectedRecording;
  }, [enrichedRecordings, selectedRecording]);

  const shareChips = [
    { id: 'all', label: 'All', count: counts.all },
    { id: 'pending', label: SHARE_STATES.pending.badge, count: counts.pending },
    { id: 'approved', label: SHARE_STATES.approved.badge, count: counts.approved },
    { id: 'excluded', label: SHARE_STATES.excluded.badge, count: counts.excluded },
  ];

  return (
    <div className={`rp-page ${mobilePane === 'detail' ? 'rp-detail-mode' : ''}`}>
      <header className="rp-header">
        <div className="rp-header-title">
          <h1>My Recordings</h1>
          <span className="rp-privacy-notice">
            <Lock size={12} /> Only you can see these
          </span>
        </div>
        <div className="rp-header-actions">
          <button
            className="rp-sync-btn"
            onClick={() => syncMutation.mutate()}
            disabled={syncMutation.isPending}
            title="Pull new recordings from Plaud now (also backfills missing AI notes)"
          >
            {syncMutation.isPending
              ? <Loader2 size={16} className="rp-spin" />
              : <CloudDownload size={16} />}
            <span className="rp-btn-label">{syncMutation.isPending ? 'Syncing…' : 'Sync from Plaud'}</span>
          </button>
          <div className="rp-export-wrapper">
            <button
              className={`rp-export-btn ${exportSuccess ? 'success' : ''}`}
              onClick={() => setShowExportMenu(!showExportMenu)}
              disabled={exporting || recordings.length === 0}
              title="Export for Google Docs / NotebookLM"
            >
              {exporting ? (
                <Loader2 size={16} className="rp-spin" />
              ) : exportSuccess ? (
                <Check size={16} />
              ) : (
                <Download size={16} />
              )}
              <span className="rp-btn-label">Export</span>
            </button>
            {showExportMenu && (
              <>
                <div className="rp-export-backdrop" onClick={() => setShowExportMenu(false)} />
                <div className="rp-export-menu">
                  <div className="rp-export-menu-header">
                    <FileJson size={14} />
                    Export for Google Docs
                  </div>
                  <button onClick={() => handleExport('work')}>
                    <span>Work Recordings</span>
                    <span className="rp-export-count">{counts.work}</span>
                  </button>
                  <button onClick={() => handleExport('personal')}>
                    <span>Personal Recordings</span>
                    <span className="rp-export-count">{counts.personal}</span>
                  </button>
                  <button onClick={() => handleExport('all')}>
                    <span>All Recordings</span>
                    <span className="rp-export-count">{counts.all}</span>
                  </button>
                  <div className="rp-export-menu-footer">
                    Applies the date range only — not the search box or GAS filter.
                    Upload the JSON to Drive, then run the Apps Script.
                  </div>
                </div>
              </>
            )}
          </div>
          <button
            className="rp-refresh-btn"
            onClick={() => refetch()}
            disabled={isFetching}
            title="Refresh recordings"
            aria-label="Refresh recordings"
          >
            <RefreshCw size={16} className={isFetching ? 'rp-spin' : ''} />
          </button>
        </div>
      </header>

      {/* One wrapping row. Two fixed rows cost ~44px of pane height on every
          window, and at desktop widths everything fits on one line anyway. */}
      <div className="rp-toolbar">
          <div className="rp-search">
            <Search size={16} />
            <input
              type="text"
              placeholder="Search recordings..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              aria-label="Search recordings"
            />
          </div>

          <div className="rp-date-filter">
            <Calendar size={14} />
            <select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
              aria-label="Date range"
            >
              {DATE_RANGES.map(range => (
                <option key={range.id} value={range.id}>{range.label}</option>
              ))}
            </select>
          </div>

          {selectedIds.size > 0 && (
            <button
              className="rp-bulk-copy-btn"
              onClick={() => setShowBulkCopy(true)}
            >
              Copy All ({selectedIds.size})
            </button>
          )}

          <div className="rp-filter-group">
            <span className="rp-filter-legend" id="rp-cat-legend">Category</span>
            <div className="rp-chips" role="group" aria-labelledby="rp-cat-legend">
              <button
                className={`rp-chip ${categoryFilter === 'all' ? 'active' : ''}`}
                aria-pressed={categoryFilter === 'all'}
                onClick={() => setCategoryFilter('all')}
              >
                All <span className="rp-chip-count">{counts.all}</span>
              </button>
              <button
                className={`rp-chip ${categoryFilter === 'work' ? 'active' : ''}`}
                aria-pressed={categoryFilter === 'work'}
                onClick={() => setCategoryFilter('work')}
              >
                Work <span className="rp-chip-count">{counts.work}</span>
              </button>
              <button
                className={`rp-chip ${categoryFilter === 'personal' ? 'active' : ''}`}
                aria-pressed={categoryFilter === 'personal'}
                onClick={() => setCategoryFilter('personal')}
              >
                Personal <span className="rp-chip-count">{counts.personal}</span>
              </button>
              {/* Only surfaced when the backend couldn't categorize something —
                  otherwise these rows would vanish from both Work and Personal
                  with no way to find them. */}
              {counts.unknown > 0 && (
                <button
                  className={`rp-chip ${categoryFilter === 'unknown' ? 'active' : ''}`}
                  aria-pressed={categoryFilter === 'unknown'}
                  onClick={() => setCategoryFilter('unknown')}
                  title="No category yet — set one in the detail pane"
                >
                  Uncategorized <span className="rp-chip-count">{counts.unknown}</span>
                </button>
              )}
            </div>
          </div>

          {/* Account scoping. "Unassigned" is the work queue: those summaries
              have no agency tab to file under in GAS. */}
          <div className="rp-filter-group">
            <span className="rp-filter-legend" id="rp-acct-legend">Account</span>
            <div className="rp-chips" role="group" aria-labelledby="rp-acct-legend">
              <button
                className={`rp-chip ${accountFilter === 'all' ? 'active' : ''}`}
                aria-pressed={accountFilter === 'all'}
                onClick={() => setAccountFilter('all')}
              >
                All <span className="rp-chip-count">{counts.all}</span>
              </button>
              {counts.unassigned > 0 && (
                <button
                  className={`rp-chip rp-chip-pending ${accountFilter === 'unassigned' ? 'active' : ''}`}
                  aria-pressed={accountFilter === 'unassigned'}
                  onClick={() => setAccountFilter('unassigned')}
                  title="No account yet — these have no agency tab to appear under in GAS"
                >
                  Unassigned <span className="rp-chip-count">{counts.unassigned}</span>
                </button>
              )}
            </div>
          </div>

          <div className="rp-filter-group">
            <span className="rp-filter-legend" id="rp-gas-legend">GAS dashboard</span>
            <div className="rp-chips" role="group" aria-labelledby="rp-gas-legend">
              {shareChips.map(chip => (
                <button
                  key={chip.id}
                  className={`rp-chip rp-chip-${chip.id} ${shareFilter === chip.id ? 'active' : ''}`}
                  aria-pressed={shareFilter === chip.id}
                  onClick={() => setShareFilter(chip.id)}
                  title={SHARE_STATES[chip.id]?.listHint}
                >
                  {chip.label} <span className="rp-chip-count">{chip.count}</span>
                </button>
              ))}
            </div>
          </div>
      </div>

      {error && (
        <div className="rp-banner rp-error">
          <AlertCircle size={18} />
          <span>Failed to load recordings: {error.message}</span>
          <button onClick={() => refetch()}>Retry</button>
        </div>
      )}

      {exportError && (
        <div className="rp-banner rp-error">
          <AlertCircle size={18} />
          <span>Export failed: {exportError}</span>
          <button onClick={() => setExportError(null)}>Dismiss</button>
        </div>
      )}

      {syncError && (
        <div className="rp-banner rp-error">
          <AlertCircle size={18} />
          <span>Sync failed: {syncError.message}</span>
          <button onClick={() => syncMutation.reset()}>Dismiss</button>
        </div>
      )}

      {hiddenUndated > 0 && (
        <div className="rp-banner rp-notice">
          <Calendar size={16} />
          <span>
            {hiddenUndated} recording{hiddenUndated !== 1 ? 's' : ''} without a usable date
            {hiddenUndated !== 1 ? ' are' : ' is'} hidden by the “
            {DATE_RANGES.find(r => r.id === dateRange)?.label}” filter.
          </span>
          <button onClick={() => setDateRange('all')}>Show all time</button>
        </div>
      )}

      {syncResult && (
        <div className="rp-banner rp-sync-result">
          <Check size={16} />
          <span>
            Sync complete — {syncResult.synced ?? 0} new
            {syncResult.skipped != null ? `, ${syncResult.skipped} already synced` : ''}
            {syncResult.failed ? `, ${syncResult.failed} failed` : ''}.
          </span>
          <button onClick={() => syncMutation.reset()}>Dismiss</button>
        </div>
      )}

      <div className={`rp-content ${mobilePane === 'detail' ? 'show-detail' : 'show-list'}`}>
        <RecordingsList
          recordings={filteredRecordings}
          selectedIds={selectedIds}
          selectedRecordingId={selectedRecording?.id}
          isLoading={isLoading}
          onSelect={handleSelectRecording}
          onToggleSelect={handleToggleSelect}
          onSelectAll={handleSelectAll}
          footer={mayHaveMore && (
            <button
              className="rp-load-more"
              onClick={() => setLimit(l => l + PAGE_SIZE)}
              disabled={isFetching}
            >
              {isFetching ? <Loader2 size={14} className="rp-spin" /> : <ChevronDown size={14} />}
              Load more
            </button>
          )}
        />

        <RecordingDetail
          recording={liveSelectedRecording}
          onCategoryChange={handleCategoryChange}
          onShareChange={handleShareChange}
          shareSaving={sharePendingId === liveSelectedRecording?.id}
          onBack={handleBackToList}
          accounts={accounts}
          onAccountChange={handleAccountChange}
          accountSaving={accountPendingId === liveSelectedRecording?.id}
          onParticipantsChange={handleParticipantsChange}
          participantsSaving={participantsPendingId === liveSelectedRecording?.id}
        />
      </div>

      {showBulkCopy && (
        <BulkCopyModal
          recordings={selectedRecordings}
          onClose={() => setShowBulkCopy(false)}
        />
      )}
    </div>
  );
}

export default RecordingsPage;
